import React, { useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { TownsquareProjectAri } from '@atlassian/ari/townsquare';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionCallbacks,
	ActionRendererKeyToPayload,
	ActionStatusProps,
	ActionWith,
	MessageActionProps,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import type {
	actionCardProjectDetailsQuery,
	actionCardProjectDetailsQuery$data,
} from './__generated__/actionCardProjectDetailsQuery.graphql';
import { CardBody } from './card-body';
import { messages } from './messages';
import { CardFooterWithTooltip } from './project-status-update-card-footer';
import { CardHeader } from './project-status-update-card-header';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		color: token('color.text'),
		backgroundColor: token('elevation.surface.raised'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		transition: '200ms',
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.raised'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.250'),
		marginBottom: token('space.200'),
	},
	footer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.200'),
	},
	footerDisclaimer: {
		maxWidth: '400px',
	},
	footerButtons: {
		display: 'flex',
		gap: token('space.100'),
		flexShrink: 0,
	},
});

type Payload = ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate];

export type Props = {
	action: ActionWith<Payload>;
	fragmentKey?: actionCardProjectDetailsQuery$data | null;
	footerTooltipEnabled?: boolean | null;
} & ActionCallbacks &
	ActionStatusProps &
	Pick<MessageActionProps, 'ContentRenderer' | 'productKey' | 'cloudId' | 'aaid'>;

const ActionCardWithProjectData = ({ projectAri, ...props }: { projectAri: string } & Props) => {
	const data = useLazyLoadQuery<actionCardProjectDetailsQuery>(
		graphql`
			query actionCardProjectDetailsQuery($projectAri: String!) {
				me {
					user {
						id
					}
				}
				townsquare {
					project(ari: $projectAri) {
						...projectStatusUpdateCardHeader_AtlassianConversationAssistantMessageActions_CardHeader
						...projectStatusUpdateCardFooter_AtlassianConversationAssistantMessageActions_CardFooterWithTooltip
					}
				}
			}
		`,
		{ projectAri },
		{
			fetchPolicy: 'store-or-network',
		},
	);

	return <ActionCardContent {...props} fragmentKey={data} />;
};

const ActionCardContent = ({
	action,
	onConfirm,
	onCancel,
	getStatus,
	getReadonly,
	fragmentKey,
}: Props) => {
	const intl = useIntl();
	const status = getStatus(action);
	const readonly = getReadonly(action);
	const isSubmitting = status === 'started';
	const isDisabled = status !== 'idle' || readonly;

	const currentUserId = fragmentKey?.me?.user?.id ?? undefined;
	const projectDetails = fragmentKey?.townsquare?.project ?? undefined;

	const hasProjectAri = fragmentKey != null;
	const showHeaderAndFooter = hasProjectAri || !fg('update_action_card_write_status_skill');

	return (
		<Stack xcss={styles.container} testId="project-status-update-action-renderer.ui.action-card">
			<Box xcss={styles.content}>
				{showHeaderAndFooter && <CardHeader action={action} fragmentKey={projectDetails} />}
				<CardBody action={action} />
			</Box>
			<Box xcss={styles.footer}>
				<Box xcss={styles.footerDisclaimer}>
					<Text size="small" color="color.text.subtle">
						{intl.formatMessage(messages.disclaimer)}
					</Text>
				</Box>
				{showHeaderAndFooter && (
					<Inline xcss={styles.footerButtons}>
						<CardFooterWithTooltip
							action={action}
							onCancel={onCancel}
							onConfirm={onConfirm}
							isDisabled={isDisabled}
							isSubmitting={isSubmitting}
							currentUserId={currentUserId}
							fragmentKey={projectDetails}
						/>
					</Inline>
				)}
			</Box>
		</Stack>
	);
};

// add a function to parse the TownsquareProjectAri
const parseTownsquareProjectAri = (projectId: string, cloudId: string) => {
	try {
		return TownsquareProjectAri.create({
			siteId: cloudId,
			projectUuid: projectId,
		}).toString();
	} catch {
		// Not all hope is lost, try to parse it directly
	}

	try {
		return TownsquareProjectAri.parse(projectId).toString();
	} catch {
		return '';
	}
};

export const ActionCard = (props: Props): React.JSX.Element | null => {
	const { action, cloudId } = props;

	const projectAri = useMemo(() => {
		const projectId = action.data.projectId;
		return parseTownsquareProjectAri(projectId, cloudId);
	}, [action.data.projectId, cloudId]);

	if (!action) {
		return null;
	}

	if (!projectAri) {
		return <ActionCardContent {...props} />;
	}

	return (
		<ErrorBoundary fallback={<ActionCardContent {...props} />}>
			<ActionCardWithProjectData {...props} projectAri={projectAri} />
		</ErrorBoundary>
	);
};
